<script lang="ts">
  import { onMount } from "svelte"
  import Layout from "$app/Layouts/Admin.svelte"
  import Switch from "$app/components/Switch.svelte"
  import Tiptap from "$app/components/Tiptap.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { xhr, toast, wait } from "$app/utils"

  const emptyWC = {
    id: 0,
    signup_headline: "",
    recent_signups: [],
    show_recent_signups: false,
    app_banner: "",
    show_app_banner: false
  }

  interface WebsiteContentTypeE
    extends Omit<WebsiteContentType, "recent_signups"> {
    recent_signups: {
      value: string
      id: number
      edit: boolean
      marked: boolean
    }[]
  }

  let props = $props()
  let website_content: WebsiteContentTypeE = $state(
    props.website_content || {
      ...emptyWC
    }
  )

  let errors: any = {}

  let form = $state({})

  onMount(() => {
    form = website_content
  })

  mapRecentSignups()

  let saving = $state(false)

  const fields = [
    "signup_headline",
    "recent_signups",
    "show_recent_signups",
    "app_banner",
    "show_app_banner"
  ]

  let edit_headline = $state(false)
  let edit_app_banner = $state(false)
  let add_sign_up_message = $state(false)
  let new_sign_up_message = $state("")
  let edit_sign_up_message = $state("")

  function mapRecentSignups() {
    if (form?.recent_signups?.length > 0) {
      const d = form.recent_signups.map((s, index) => {
        if (s.value) {
          return s
        } else {
          return { value: s, id: index, edit: false, marked: false }
        }
      })

      form.recent_signups = d
    }
  }

  function getFormData(): FormData {
    const formData = new FormData()
    fields.forEach((field) => {
      if (["recent_signups"].includes(field)) {
        form[field].forEach((recent_signup) => {
          formData.append(`website_content[${field}][]`, recent_signup.value)
        })
      } else {
        formData.append(`website_content[${field}]`, form[field] || "")
      }
    })

    return formData
  }

  /** HEADLINE FUNCTIONS */
  async function saveHeadline(): Promise<void> {
    await updateWebsiteContent()
    edit_headline = false
  }

  async function cancelHeadline(): Promise<void> {
    form.signup_headline = website_content?.signup_headline
    edit_headline = false
  }

  /** END HEADLINE FUNCTIONS */

  /** SIGNUP MESSAGE FUNCTIONS */

  async function addNewSignupMessage(): Promise<void> {
    form.recent_signups = [...form.recent_signups, new_sign_up_message]
    console.log(form.recent_signups)
    mapRecentSignups()
    console.log(form.recent_signups)
    await updateWebsiteContent()
    new_sign_up_message = ""
    add_sign_up_message = false
  }

  async function deleteSignupMessage(id): Promise<void> {
    const pos = form.recent_signups.findIndex((el) => el.id === id)
    if (pos >= 0) {
      form.recent_signups[pos].marked = true
      form.recent_signups = form.recent_signups
      await wait(100)
      if (confirm("Are you sure you want to delete?")) {
        form.recent_signups.splice(pos, 1)
        await updateWebsiteContent()
      } else {
        form.recent_signups[pos].marked = false
        form.recent_signups = form.recent_signups
      }
    }
  }

  async function editSignupMessage(id): Promise<void> {
    const pos = form.recent_signups.findIndex((el) => el.id === id)
    if (pos >= 0) {
      edit_sign_up_message = form.recent_signups[pos].value
      form.recent_signups[pos].edit = true
    }
  }

  async function updateSignupMessage(id): Promise<void> {
    const pos = form.recent_signups.findIndex((el) => el.id === id)
    if (pos >= 0) {
      form.recent_signups[pos].value = edit_sign_up_message
      form.recent_signups[pos].edit = false
      edit_sign_up_message = ""
      await updateWebsiteContent()
    }
  }

  async function cancelEditSignupMessage(id): Promise<void> {
    const pos = form.recent_signups.findIndex((el) => el.id === id)
    if (pos >= 0) {
      form.recent_signups[pos].edit = false
      edit_sign_up_message = ""
    }
  }

  async function cancelNewSignupMessage(): Promise<void> {
    new_sign_up_message = ""
    add_sign_up_message = false
  }

  /** END SIGNUP MESSAGE FUNCTIONS */

  /** WEBSITE BANNER FUNCTIONS */

  async function cancelBanner(): Promise<void> {
    form.app_banner = website_content.app_banner
    edit_app_banner = false
  }

  /** END WEBSITE BANNER FUNCTIONS */

  async function updateWebsiteContent(): Promise<void> {
    saving = true
    errors = {}

    try {
      const resp = await xhr.patch(
        `/admin/website_content/${website_content.id}`,
        getFormData()
      )

      website_content = resp.data
      form = { ...website_content }
      mapRecentSignups()

      toast("Website Content Updated", "success")
    } catch (e) {
      toast("There was an error saving the web content", "error")
      console.error(e)
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
      edit_app_banner = false
    }
  }
</script>

<Layout {...props}>
  <span slot="title">Website Content</span>

  <div class="bg-white py-4 mt-8 px-4 sm:px-6 rounded-lg text-gray-600">
    {#if edit_headline}
      <div class="flex justify-start gap-x-8">
        <div>
          <h2 class="text-base font-semibold leading-6 text-gray-900">
            Sign-Up Headline
          </h2>
        </div>
      </div>

      <div class="w-full">
        <div class="rounded mt-2">
          <textarea
            placeholder="Enter a headline"
            bind:value={form.signup_headline}
          ></textarea>
        </div>
      </div>
      <div class="flex justify-start items-start gap-x-2 mt-2">
        <button
          onclick={saveHeadline}
          class="py-1 px-4 bg-green-600 text-white rounded">Save</button
        >
        <button
          onclick={cancelHeadline}
          class="py-1 px-4 bg-gray-300 text-gray-800 rounded">Cancel</button
        >
      </div>
    {:else}
      <div class="flex justify-between gap-x-8 items-center">
        <h2 class="text-base font-semibold leading-6 text-gray-900">
          Sign-Up Headline
        </h2>
        <div class="flex justify-center">
          <button
            onclick={() => (edit_headline = true)}
            class="py-1 px-4 bg-blue-600 text-white rounded"
            >Edit Headline</button
          >
        </div>
      </div>

      <div class="grid grid-cols-4 w-full mt-4">
        {#if website_content?.signup_headline}
          <div
            class=" bg-white border border-gray-200 rounded py-3 px-6 mt-2 col-span-4 text-xl font-bold"
          >
            {website_content.signup_headline}
          </div>
        {:else}
          <div class="bg-gray-200 rounded py-2 px-4 mt-2 col-span-3">
            No headline configured
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <div class="bg-white py-4 mt-8 px-4 sm:px-6 rounded-lg text-gray-600">
    <h2 class="text-base font-semibold leading-6 text-gray-900">
      Recent Sign-Up Messages
    </h2>
    <div class="flex gap-2 items-center py-2">
      <p class="text-base">Sign-up Popups Enabled?</p>
      <Switch
        active={form.show_recent_signups}
        onchange={(active) => {
          updateWebsiteContent()
        }}
      />
      <button
        onclick={() => (form.show_recent_signups = !form.show_recent_signups)}
        class="cursor-pointer flex items-center pt-0"
        class:text-red-500={!form.show_recent_signups}
        class:text-green-500={form.show_recent_signups}
      >
        {form.show_recent_signups ? "Yes" : "No"}
      </button>
    </div>
    <div class="rounded py-2 px-4 mt-2">
      <ul class="gap-2">
        {#each form.recent_signups as recent_signup}
          <li class="py-2 flex justify-between gap-x-4">
            <span
              class="bg-gray-200 py-2 px-4 w-full rounded {recent_signup.marked
                ? 'border border-red-400 bg-red-800 text-white'
                : ''}"
            >
              {#if recent_signup.edit}
                <textarea
                  class="text-black"
                  placeholder="Enter recent signup message"
                  bind:value={edit_sign_up_message}
                ></textarea>
              {:else}
                {recent_signup.value}
              {/if}
            </span>
            <div class="flex gap-x-2">
              {#if recent_signup.edit}
                <button
                  onclick={() => updateSignupMessage(recent_signup.id)}
                  class="py-1 px-4 bg-green-600 text-white rounded">Save</button
                >
                <button
                  onclick={() => cancelEditSignupMessage(recent_signup.id)}
                  class="py-1 px-4 bg-gray-300 text-gray-800 rounded"
                  >Cancel</button
                >
              {:else}
                <button
                  class="bg-blue-200 rounded text-gray-700 py-1 px-3 text-sm"
                  onclick={() => editSignupMessage(recent_signup.id)}
                  >Edit</button
                >
                <button
                  class="bg-red-300 rounded text-gray-700 py-1 px-3 text-sm"
                  onclick={() => deleteSignupMessage(recent_signup.id)}
                  >Delete</button
                >
              {/if}
            </div>
          </li>
        {:else}
          <div
            class="border-indigo-100 border rounded py-2 px-6 bg-indigo-50 italic text-sm"
          >
            No Recent Sign-Up Messages
          </div>
        {/each}
      </ul>
    </div>

    <div class="py-2">
      {#if add_sign_up_message}
        <div class="mt-2">
          <textarea
            class="text-black"
            placeholder="Enter recent signup message"
            bind:value={new_sign_up_message}
          ></textarea>
        </div>
        <div class="flex justify-end items-start gap-x-2 mt-2">
          <button
            onclick={addNewSignupMessage}
            class="py-1 px-4 bg-green-600 text-white rounded">Save</button
          >
          <button
            onclick={cancelNewSignupMessage}
            class="py-1 px-4 bg-gray-300 text-gray-800 rounded">Cancel</button
          >
        </div>
      {:else}
        <div class="flex justify-end items-start gap-x-2 mt-2">
          <button
            onclick={() => (add_sign_up_message = true)}
            class="py-1 px-4 bg-blue-600 text-white rounded"
            >Add New Sign-Up Message</button
          >
        </div>
      {/if}
    </div>
  </div>

  <div class="bg-white py-4 mt-8 px-4 sm:px-6 rounded-lg text-gray-600">
    <h2 class="text-base font-semibold leading-6 text-gray-900">App Banner</h2>
    <div class="flex gap-2 items-center py-2">
      <p class="text-base">App Banner Enabled?</p>
      <Switch
        active={form.show_app_banner}
        onchange={(active) => {
          updateWebsiteContent()
        }}
      />
      <button
        onclick={() => (form.show_app_banner = !form.show_app_banner)}
        class="cursor-pointer flex items-center pt-0"
        class:text-red-500={!form.show_app_banner}
        class:text-green-500={form.show_app_banner}
      >
        {form.show_app_banner ? "Yes" : "No"}
      </button>
    </div>

    {#if edit_app_banner}
      <div class="flex justify-end gap-x-8">
        <div class="flex justify-center items-start gap-x-2">
          <button
            onclick={cancelBanner}
            class="py-1 px-4 bg-gray-300 text-gray-800 rounded"
            >Cancel Editing</button
          >
        </div>
      </div>

      <div class="grid grid-cols-4 w-full">
        <div class="mt-2 col-span-4 z-0">
          <Tiptap
            id={`website-content`}
            content={form.app_banner}
            onupdate={({ html }) => (form.app_banner = html)}
            onsave={updateWebsiteContent}
            buttons={[
              ["bold", "italic", "underline", "strike"],
              ["bulletList", "orderedList", "blockquote"],
              ["link", "unlink", "image"]
            ]}
            allowFileUploads={true}
            allowEmojis={true}
            showSaveArrow={true}
            allowGiphy={true}
          />
        </div>
      </div>
    {:else}
      <div class="flex justify-end gap-x-8">
        <div class="flex justify-center">
          <button
            onclick={() => (edit_app_banner = true)}
            class="py-1 px-4 bg-blue-600 text-white rounded">Edit Banner</button
          >
        </div>
      </div>

      <div class="grid grid-cols-4 w-full mt-2" id="app-banner">
        {#if website_content.app_banner}
          <div
            class="bg-lwp-blue text-white ProseMirror rounded py-2 px-4 text-center mt-2 col-span-4"
          >
            {@html website_content.app_banner}
          </div>
        {:else}
          <div
            class="bg-gray-100 text-gray-700 rounded py-2 px-4 mt-2 col-span-2"
          >
            No app banner configured
          </div>
        {/if}
      </div>
    {/if}
  </div>
</Layout>
